import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const flipperContractorsApi = createApi({
  reducerPath: "flipperContractorsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["FlipperContractor", "FlipperContractorStats"],
  endpoints: (builder) => ({
    // Get all contractors
    getAllContractors: builder.query({
      query: ({ specialty, status, search, page = 1, limit = 100 } = {}) => {
        const params = new URLSearchParams();
        if (specialty && specialty !== "all") params.append("specialty", specialty);
        if (status && status !== "all") params.append("status", status);
        if (search) params.append("search", search);
        params.append("page", page);
        params.append("limit", limit);
        return `/flipper/contractors?${params.toString()}`;
      },
      providesTags: (result) =>
        result?.contractors
          ? [
              ...result.contractors.map(({ _id }) => ({ type: "FlipperContractor", id: _id })),
              { type: "FlipperContractor", id: "LIST" },
            ]
          : [{ type: "FlipperContractor", id: "LIST" }],
    }),

    // Get contractor by ID
    getContractorById: builder.query({
      query: (id) => `/flipper/contractors/${id}`,
      providesTags: (result, error, id) => [{ type: "FlipperContractor", id }],
    }),

    // Create contractor
    createContractor: builder.mutation({
      query: (contractorData) => ({
        url: "/flipper/contractors",
        method: "POST",
        body: contractorData,
      }),
      invalidatesTags: [
        { type: "FlipperContractor", id: "LIST" },
        { type: "FlipperContractorStats" },
      ],
    }),

    // Update contractor
    updateContractor: builder.mutation({
      query: ({ id, ...contractorData }) => ({
        url: `/flipper/contractors/${id}`,
        method: "PUT",
        body: contractorData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperContractor", id },
        { type: "FlipperContractor", id: "LIST" },
        { type: "FlipperContractorStats" },
      ],
    }),

    // Delete contractor
    deleteContractor: builder.mutation({
      query: (id) => ({
        url: `/flipper/contractors/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "FlipperContractor", id: "LIST" },
        { type: "FlipperContractorStats" },
      ],
    }),

    // Update contractor status
    updateContractorStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/flipper/contractors/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "FlipperContractor", id },
        { type: "FlipperContractor", id: "LIST" },
        { type: "FlipperContractorStats" },
      ],
    }),

    // Increment projects completed
    incrementProjectsCompleted: builder.mutation({
      query: (id) => ({
        url: `/flipper/contractors/${id}/increment-projects`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "FlipperContractor", id },
        { type: "FlipperContractorStats" },
      ],
    }),

    // Get stats
    getContractorStats: builder.query({
      query: () => "/flipper/contractors/stats",
      providesTags: [{ type: "FlipperContractorStats" }],
    }),
  }),
});

export const {
  useGetAllContractorsQuery,
  useGetContractorByIdQuery,
  useCreateContractorMutation,
  useUpdateContractorMutation,
  useDeleteContractorMutation,
  useUpdateContractorStatusMutation,
  useIncrementProjectsCompletedMutation,
  useGetContractorStatsQuery,
} = flipperContractorsApi;
